// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, type ReactNode } from "react"
import { $isRangeSelection, type BaseSelection, FORMAT_TEXT_COMMAND, type TextFormatType } from "lexical"
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
} from "lucide-react"
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from "@lexical/list"
import { $setBlocksType } from "@lexical/selection"
import { $createHeadingNode, type HeadingTagType } from "@lexical/rich-text"
import { $createParagraphNode, $getSelection } from "lexical"

import { useToolbarContext } from "@/components/editor/context/toolbar-context"
import { useUpdateToolbarHandler } from "@/components/editor/editor-hooks/use-update-toolbar"
import { Separator } from "@/components/ui/separator"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const TEXT_FORMAT_BUTTONS: Array<{
  format: TextFormatType
  icon: ReactNode
  name: string
  shortcut?: string
}> = [
  { format: "bold", icon: <Bold className="size-4" />, name: "Bold", shortcut: "Ctrl+B" },
  { format: "italic", icon: <Italic className="size-4" />, name: "Italic", shortcut: "Ctrl+I" },
  { format: "underline", icon: <Underline className="size-4" />, name: "Underline", shortcut: "Ctrl+U" },
  { format: "strikethrough", icon: <Strikethrough className="size-4" />, name: "Strikethrough" },
  { format: "code", icon: <Code className="size-4" />, name: "Code" },
]

const BLOCK_TYPE_OPTIONS: Array<{
  value: "paragraph" | "h1" | "h2" | "h3" | "ul" | "ol"
  label: string
  icon?: ReactNode
}> = [
  { value: "paragraph", label: "Normal" },
  { value: "h1", label: "Heading 1", icon: <Heading1 className="size-4" /> },
  { value: "h2", label: "Heading 2", icon: <Heading2 className="size-4" /> },
  { value: "h3", label: "Heading 3", icon: <Heading3 className="size-4" /> },
  { value: "ul", label: "Bullet List", icon: <List className="size-4" /> },
  { value: "ol", label: "Numbered List", icon: <ListOrdered className="size-4" /> },
]

export function TextFormatToolbarPlugin({
  formats = TEXT_FORMAT_BUTTONS.map(option => option.format),
  separator = true,
  showBlockTypes = true,
}: {
  formats?: TextFormatType[]
  separator?: boolean
  showBlockTypes?: boolean
}) {
  const { activeEditor } = useToolbarContext()
  const [activeFormats, setActiveFormats] = useState<TextFormatType[]>([])
  const [blockType, setBlockType] = useState<string>("paragraph")

  const $updateToolbar = (selection: BaseSelection) => {
    if ($isRangeSelection(selection)) {
      // Update text formatting
      const formats: TextFormatType[] = []
      if (selection.hasFormat("bold")) formats.push("bold")
      if (selection.hasFormat("italic")) formats.push("italic")
      if (selection.hasFormat("underline")) formats.push("underline")
      if (selection.hasFormat("strikethrough")) formats.push("strikethrough")
      if (selection.hasFormat("code")) formats.push("code")
      setActiveFormats(formats)

      // Update block type
      const anchorNode = selection.anchor.getNode()
      const element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow()
      const elementKey = element.getKey()
      const elementDOM = activeEditor.getElementByKey(elementKey)

      if (elementDOM !== null) {
        const type = element.getType()
        if (type === "heading") {
          const tag = (element as any).getTag?.()
          setBlockType(tag || "paragraph")
        } else if (type === "list") {
          const parentList = (element as any).__parent
          setBlockType(parentList?.__listType === "number" ? "ol" : "ul")
        } else {
          setBlockType("paragraph")
        }
      }
    }
  }

  useUpdateToolbarHandler($updateToolbar)

  const formatText = (format: TextFormatType) => {
    activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
  }

  const formatBlock = (value: string) => {
    setBlockType(value)

    if (value === "ul") {
      activeEditor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    } else if (value === "ol") {
      activeEditor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    } else {
      activeEditor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          if (value === "paragraph") {
            $setBlocksType(selection, () => $createParagraphNode())
          } else if (value.startsWith("h")) {
            $setBlocksType(selection, () => $createHeadingNode(value as HeadingTagType))
          }
        }
      })
    }
  }

  return (
    <>
      {showBlockTypes && (
        <Select value={blockType} onValueChange={formatBlock}>
          <SelectTrigger
            aria-label="Text style"
            className="h-9 w-[160px] justify-between rounded-md border border-border/60 bg-background px-3 text-xs font-medium shadow-sm"
          >
            <SelectValue placeholder="Text style" />
          </SelectTrigger>
          <SelectContent>
            {BLOCK_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value} className="text-xs">
                <div className="flex items-center gap-2">
                  {option.icon && option.icon}
                  <span>{option.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {showBlockTypes && separator && <Separator orientation="vertical" className="!h-7" />}

      {/* Text formatting toggles */}
      <ToggleGroup
        type="multiple"
        value={activeFormats}
        className="flex items-center gap-1.5 rounded-md border border-border/60 bg-background/80 px-1.5 py-1 shadow-sm"
      >
        {TEXT_FORMAT_BUTTONS.filter(option => formats.includes(option.format)).map((option) => (
          <ToggleGroupItem
            key={option.format}
            value={option.format}
            variant="outline"
            size="sm"
            aria-label={option.name}
            title={option.shortcut ?? option.name}
            onClick={() => formatText(option.format)}
            className="h-8 w-8 p-0 data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary"
          >
            {option.icon}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </>
  )
}
